import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ICallPanel } from "../models";
import { api } from "../services/api";
import {
  getHealth as getAudioHealth,
  isAudioActive,
} from "../services/audio/audioEngine";
import { isSpeaking } from "../services/audio/speechEngine";
import { isHealthy as isSocketHealthy } from "../services/socket/socketManager";
import { telemetry } from "../services/telemetry/telemetry";

interface IPanelContext {
  isLoading: boolean;
  isRegistered: () => boolean;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsConnected: (value: boolean) => void;
  isConnected: boolean;
  panel: ICallPanel | null;
  handleRegisterPanel: (panel: ICallPanel | null) => Promise<boolean>;
  handleClosePanel: () => Promise<void>;
  setActiveCall: (active: boolean) => void;
}

interface IPanelProvider {
  children: ReactNode;
}

const TIME_LIMIT = 120;
const TIME_INITIAL = 1;
const HARD_RELOAD_AFTER_S = 600;
const ANTI_LOOP_BASE_MS = 60 * 1000; // 1 min base
const ANTI_LOOP_MAX_MS = 30 * 60 * 1000; // 30 min cap (servidor em manutencao)
const ANTI_LOOP_KEY = "panel@lastReloadAt";
const RELOAD_ATTEMPTS_KEY = "panel@reloadAttempts";
const REST_FAILURE_THRESHOLD = 3;

// Backoff exponencial entre reloads consecutivos. Reseta para 0 assim
// que o REST volta a responder OK (servidor saiu da manutencao).
//   attempts=0 -> 1 min
//   attempts=1 -> 2 min
//   attempts=2 -> 4 min
//   attempts=3 -> 8 min
//   attempts=4 -> 16 min
//   attempts>=5 -> 30 min (cap)
function computeAntiLoopWindow(attempts: number): number {
  const ms = ANTI_LOOP_BASE_MS * Math.pow(2, Math.max(0, attempts));
  return Math.min(ms, ANTI_LOOP_MAX_MS);
}

export const PanelContext = createContext<IPanelContext>({} as IPanelContext);

export const PanelProvider: React.FC<IPanelProvider> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [panel, setPanel] = useState<ICallPanel | null>(null);
  const [clock, setClock] = useState<number>(TIME_INITIAL);

  const activeCallRef = useRef<boolean>(false);
  const restFailuresRef = useRef<number>(0);
  const secondsSinceReloadRef = useRef<number>(0);

  const setActiveCall = useCallback((active: boolean) => {
    activeCallRef.current = active;
  }, []);

  const handleRegisterPanel = async (panel: ICallPanel | null) => {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        setIsLoading(true);

        localStorage.setItem("panel@drclick", JSON.stringify(panel));
        await getPanel();
        setIsOpen(true);
        resolve(true);
      } catch (error: any) {
        setIsLoading(false);
        if (error.response) {
          reject(error.response.data.error.message);
        }
      }
    });
  };

  const handleClosePanel = async () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        setIsLoading(true);

        localStorage.removeItem("panel@drclick");
        window.location.reload();
        resolve();
      } catch (error: any) {
        setIsLoading(false);
      }
    });
  };

  const getPanel = async () => {
    setIsLoading(true);
    const panelStorage = localStorage.getItem("panel@drclick");
    if (panelStorage !== null) {
      try {
        const res = await api.get(
          `/users-patients/call-panel-by-id?idpainelchamado=${
            JSON.parse(panelStorage).idpainelchamado
          }`
        );
        setPanel(res.data.data);
        setIsLoading(false);
        setIsConnected(true);
      } catch (error: any) {
        if (error?.response?.data?.status === 404) {
          localStorage.clear();
        }
        setIsLoading(false);
        setIsConnected(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const performReload = useCallback((reason: Record<string, any>) => {
    try {
      localStorage.setItem(ANTI_LOOP_KEY, String(Date.now()));
      // Incrementa contador de tentativas para que o proximo reload
      // espere mais (backoff exponencial). Reseta quando REST voltar OK.
      const prev = Number(localStorage.getItem(RELOAD_ATTEMPTS_KEY) || 0);
      localStorage.setItem(RELOAD_ATTEMPTS_KEY, String(prev + 1));
    } catch {
      // localStorage pode falhar em modo privado; nao interrompe o reload.
    }
    telemetry("reload_executed", reason);
    window.location.reload();
  }, []);

  const validateConnection = useCallback(async () => {
    const panelStorage = localStorage.getItem("panel@drclick");
    if (panelStorage === null) {
      setIsConnected(false);
      return;
    }

    let restOk = false;
    try {
      await api.get(
        `/users-patients/call-panel-by-id?idpainelchamado=${
          JSON.parse(panelStorage).idpainelchamado
        }`
      );
      restOk = true;
      setIsConnected(true);
      restFailuresRef.current = 0;
      // Servidor respondeu OK: zera o contador de reloads consecutivos
      // (caso estivessemos em manutencao, agora o backoff volta ao zero).
      try {
        localStorage.setItem(RELOAD_ATTEMPTS_KEY, "0");
      } catch {
        // ignore
      }
    } catch (error: any) {
      if (error?.response?.data?.status === 404) {
        localStorage.clear();
        return;
      }
      setIsConnected(false);
      restFailuresRef.current += 1;
    }

    // Anti-loop com backoff exponencial. Se o servidor estiver em
    // manutencao prolongada, espacamos cada vez mais entre reloads em
    // vez de ficar dando F5 freneticamente.
    try {
      const reloadAttempts = Number(
        localStorage.getItem(RELOAD_ATTEMPTS_KEY) || 0
      );
      const antiLoopWindow = computeAntiLoopWindow(reloadAttempts);
      const lastReloadAt = Number(localStorage.getItem(ANTI_LOOP_KEY) || 0);
      if (lastReloadAt && Date.now() - lastReloadAt < antiLoopWindow) {
        telemetry("reload_suspended_after_recent", {
          lastReloadAt,
          waitMs: antiLoopWindow,
          reloadAttempts,
        });
        return;
      }
    } catch {
      // ignore
    }

    const sockOk = isSocketHealthy();
    const audio = getAudioHealth();
    const audioOk =
      audio.consecutiveFailures < 3 &&
      (audio.lastSuccessAgeMs === null ||
        audio.lastSuccessAgeMs < 10 * 60 * 1000);
    const systemHealthy = restOk && sockOk && audioOk;
    const audioBusy = isAudioActive() || isSpeaking();

    if (
      (activeCallRef.current || audioBusy) &&
      systemHealthy &&
      secondsSinceReloadRef.current < HARD_RELOAD_AFTER_S
    ) {
      telemetry("reload_skipped_active_call", {
        restOk,
        sockOk,
        audioOk,
        activeCall: activeCallRef.current,
        audioBusy,
        secondsSinceReload: secondsSinceReloadRef.current,
      });
      // Tenta de novo em 10s.
      setClock(TIME_LIMIT - 10);
      return;
    }

    // Sistema saudavel e ocioso: nao precisa reloadar. Socket reconecta
    // sozinho e rehydrate cobre drift de estado. Reload preventivo so
    // causa overlay de unlock reaparecendo a toa.
    if (
      systemHealthy &&
      secondsSinceReloadRef.current < HARD_RELOAD_AFTER_S
    ) {
      telemetry("reload_skipped_active_call", {
        restOk,
        sockOk,
        audioOk,
        activeCall: activeCallRef.current,
        audioBusy,
        secondsSinceReload: secondsSinceReloadRef.current,
      });
      return;
    }

    if (
      !restOk &&
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      // Offline real: nao adianta reload, segue tentando no proximo ciclo.
      return;
    }

    if (!restOk && restFailuresRef.current < REST_FAILURE_THRESHOLD) {
      // Aguarda mais um ciclo antes de reloadar quando REST esta intermitente.
      return;
    }

    secondsSinceReloadRef.current = 0;
    performReload({ restOk, sockOk, audioOk });
  }, [performReload]);

  const isRegistered = () => {
    const totemStorage = localStorage.getItem("panel@drclick");
    return totemStorage === null ? false : true;
  };

  useEffect(() => {
    getPanel();
  }, []);

  useEffect(() => {
    let time: any = null;
    if (panel) {
      if (clock < TIME_LIMIT) {
        time = setInterval(() => {
          setClock((state) => state + 1);
          secondsSinceReloadRef.current += 1;
        }, 1000);
      } else {
        setClock(TIME_INITIAL);
        validateConnection();
      }

      return () => clearInterval(time);
    }
  }, [clock, panel, validateConnection]);

  return (
    <PanelContext.Provider
      value={{
        isLoading,
        panel,
        isOpen,
        setIsOpen,
        isConnected,
        setIsConnected,
        isRegistered,
        handleRegisterPanel,
        handleClosePanel,
        setActiveCall,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};

export const usePanel = () => {
  return useContext(PanelContext);
};
